import React from 'react';

import { FlexBox, List, Message, MessageProps, Orientation, Size, Text, Typography } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import { AI } from '../../keys';
import { AiAuthorizationError } from '../../types';
import { AiAuthorizationErrorCard } from './AiAuthorizationErrorCard';
import { AiAuthorizationSuccessCard } from './AiAuthorizationSuccessCard';

interface AiAuthorizationErrorsListProps {
    /** List of authorization errors of sources used in an AI query */
    authErrors?: AiAuthorizationError[];
    /** Method used to remake the AI query */
    refetch?: () => void;
    /** Kind of the message wrapping the connect cards. Defaults to `warning`. */
    kind?: MessageProps['kind'];
    /** Message displayed above the list of connect cards. Defaults to the standard authorization warning. */
    errorMessage?: string;
    /** Message displayed in the success card once a connector is connected. Defaults to the standard success message. */
    successMessage?: string;
    /** Custom icon for success refetch button. Defaults to mdiReload. Pass null to render no icon. */
    successButtonIcon?: string | null;
    /** Custom message for success refetch action */
    successButtonLabel?: string;
}

/**
 * Component displayed in an AI answer if there are `authErrors`.
 * Initially displays a list of `AiAuthorizationErrorCard`s for each errored connector.
 * If at least one errored connector is connected, displays `AiAuthorizationSuccessCard`.
 * If all errored connectors are connected, the list of `AiAuthorizationErrorCard`s is not displayed anymore.
 *
 * @param {AiAuthorizationErrorsListProps} props - Props including `authErrors` and optional `refetch` method.
 * @returns {AiAuthorizationErrorsList} - The rendered component.
 */
export const AiAuthorizationErrorsList = ({
    authErrors,
    refetch,
    kind = 'warning',
    errorMessage,
    successMessage,
    successButtonIcon,
    successButtonLabel,
}: AiAuthorizationErrorsListProps) => {
    const { translateKey } = useTranslate();

    const [erroredConnectors, setErroredConnectors] = React.useState<{ id: string; isConnected: boolean }[]>(
        authErrors?.map((connector) => ({
            id: connector.sourceConnector.id,
            /**
             * The backend is the source of truth.
             * If a connector is received in `authErrors` we automatically assume it's not connected.
             * Further authorization check is made within the `AiAuthorizationErrorCard` component.
             */
            isConnected: false,
        })) ?? [],
    );

    /** Boolean indicating whether at least one errored connector is connected */
    const oneConnected = React.useMemo(() => erroredConnectors?.some((c) => c.isConnected), [erroredConnectors]);

    /** Boolean indicating whether all errored connectors are connected */
    const allConnected = React.useMemo(() => !erroredConnectors?.some((c) => !c.isConnected), [erroredConnectors]);

    return (
        <FlexBox orientation={Orientation.vertical} gap={Size.big}>
            {oneConnected && (
                <AiAuthorizationSuccessCard
                    refetch={refetch}
                    message={successMessage}
                    buttonIcon={successButtonIcon}
                    refetchButtonLabel={successButtonLabel}
                />
            )}
            {!allConnected && (
                <Message kind={kind} hasBackground>
                    <Text as="p" typography={Typography.body1}>
                        {errorMessage || translateKey(AI.AUTHORIZATION_ERROR_WARNING)}
                    </Text>
                    <List>
                        {authErrors?.map((authError) => (
                            <AiAuthorizationErrorCard
                                key={authError.sourceConnector.id}
                                connector={authError.sourceConnector}
                                setErroredConnectors={setErroredConnectors}
                                isConnected={
                                    erroredConnectors.find((c) => c.id === authError.sourceConnector.id)?.isConnected ??
                                    false
                                }
                            />
                        ))}
                    </List>
                </Message>
            )}
        </FlexBox>
    );
};
